/**
 * Main functions to call GestionaleAuto APIs
 *
 * @author Gerardo Aprili <gerardo@gestionaleauto.com>
 * @author Gian Luca Dalla Torre <gianluca@gestionaleauto.com>
 */
import _ from 'lodash';
import axios from 'axios';
import qs from 'qs';
import HttpStatus from 'http-status-codes';
/**
 * Store
 */
import getStore from '../redux/store';
/**
 * Helpers
 */
import { getToken } from './auth';

// eslint-disable-next-line import/no-named-default
import { default as appConfig } from '../config';

/**
 * Private function performing the axios call to the API.
 *
 * @param method {String}             // Http request method (defined in export)
 * @param entryPoint {String}         // Entrypoint to hit
 * @param headers {Object}            // HTTP headers
 * @param params {Object}             // HTTP query string parameters (get)
 * @param data {Object}               // HTTP (form) data
 * @param stringifyQuery {Boolean}    // Whether data is sent as query string or json
 * @param silentFail {Boolean}        // Flag to ignore errors and do not show message
 * @param responseType
 * @param patchMultipart {Boolean}    // If true do PATCH call for multipart instead of default POST
 * @param signal
 * @param pgp {Boolean}               // If true PriceGuruPro API will be used instead of default
 *
 * @returns {Promise<AxiosResponse<any>>}
 * @private
 */

// eslint-disable-next-line no-underscore-dangle
const _call = ({
  method,
  entryPoint,
  headers = {},
  params,
  data,
  stringifyQuery = false,
  silentFail = false,
  responseType = null,
  patchMultipart = false,
  signal = null,
  pgp = false,
  absolute = false,
}) => {
  if (!_.isString(entryPoint)) {
    throw new Error('Failed call, entryPoint parameter missing');
  }
  let newMethod = method;

  const url = (pgp ? appConfig.pgpApiBaseUrl : appConfig.apiBaseUrl) + entryPoint;
  const newHeaders = { ...headers };

  if (!pgp) {
    const s = getStore();
    const { leadId } = s.getState().lead;
    if (!newHeaders['ga-token']) {
      newHeaders['ga-token'] = getToken(leadId);
    }
  }
  if (method === 'multipart') {
    newHeaders['Content-Type'] = 'multipart/form-data';
    newMethod = patchMultipart ? 'patch' : 'post';
  }
  axios.interceptors.response.use((response) => response, async (error) => {
    // Retrieve silentFail value from request config
    const errorSilentFail = error.config?.silentFail ?? false;

    const status = error?.response?.status;
    // Manage a canceled call
    if (!status && error.message === 'canceled') {
      return error.response;
    }
    switch (status) {
      case HttpStatus.UNAUTHORIZED:
        console.log('Error 401');
        break;
      case HttpStatus.FORBIDDEN:
        // Refresh permissions
        break;
      case HttpStatus.INTERNAL_SERVER_ERROR:
        console.log('Error 500');
        break;
      default: {
        console.log('Default error: %o (silentFail = %o)', status, errorSilentFail);
      }
    }
    return error.response;
  });
  // Perform call
  return axios.request({
    method: newMethod,
    url: absolute ? entryPoint : url,
    headers: newHeaders,
    responseType,
    params,
    paramsSerializer: (cbParams) => qs.stringify(cbParams, { indices: false }),
    data: stringifyQuery ? qs.stringify(data) : data,
    silentFail,
    signal,
  });
};

export default {
  get: (config) => _call({
    ...config,
    method: 'get',
  }),
  post: (config) => _call({
    ...config,
    method: 'post',
  }),
  patch: (config) => _call({
    ...config,
    method: 'patch',
  }),
  delete: (config) => _call({
    ...config,
    method: 'delete',
  }),
  multipart: (config) => _call({
    ...config,
    method: 'multipart',
  }),
};
